import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronLeft, ChevronRight, Download, Play } from "lucide-react";

const FILTERS = [
  ["all", "All"],
  ["running", "Running"],
  ["stops", "Stops"],
  ["speeding", "Speeding"],
  ["parking", "Parking"],
  ["idle", "Idle"],
];

const TAB_LABELS = [
  ["playback", "Timeline"],
  ["events", "Events"],
  ["parking", "Parking"],
];

const PAGE_SIZE = 10;

export default function HistoryTimelinePanel({
  activeTab = "playback",
  onTabChange,
  rows = [],
  currentIndex = 0,
  onJumpToIndex,
  onExport,
  markers = [],
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setActiveFilter("all");
    setCurrentPage(1);
  }, [activeTab]);

  const filteredRows = useMemo(() => {
    if (activeFilter === "all") return rows;
    return rows.filter((row) => row.filterType === activeFilter || row.statusTone === activeFilter);
  }, [activeFilter, rows]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter, rows]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));

  useEffect(() => {
    setCurrentPage((prev) => Math.min(prev, totalPages));
  }, [totalPages]);

  const visibleRows = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredRows.slice(start, start + PAGE_SIZE);
  }, [currentPage, filteredRows]);

  const pageStart = filteredRows.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const pageEnd = filteredRows.length === 0 ? 0 : Math.min(currentPage * PAGE_SIZE, filteredRows.length);

  const pageNumbers = useMemo(() => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    if (currentPage <= 3) return [1, 2, 3, 4, totalPages];
    if (currentPage >= totalPages - 2) return [1, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    return [1, currentPage - 1, currentPage, currentPage + 1, totalPages];
  }, [currentPage, totalPages]);

  return (
    <section className={`history-timeline-panel ${collapsed ? "is-collapsed" : ""}`}>
      <button type="button" className="history-panel-handle" onClick={() => setCollapsed((prev) => !prev)}>
        <ChevronDown size={18} />
      </button>

      <div className="history-panel-header">
        <div className="history-panel-tabs">
          {TAB_LABELS.map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={activeTab === key ? "active" : ""}
              onClick={() => onTabChange?.(key)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="history-panel-tools">
          <div className="history-panel-filters">
            {FILTERS.map(([key, label]) => (
              <button
                key={key}
                type="button"
                className={activeFilter === key ? "active" : ""}
                onClick={() => setActiveFilter(key)}
              >
                {label}
              </button>
            ))}
          </div>
          <button type="button" className="history-panel-export" onClick={onExport}>
            <Download size={14} />
            Export Report
          </button>
        </div>
      </div>

      {!collapsed ? (
        <>
          <div className="history-marker-strip">
            <div className="history-marker-time">12:00 AM</div>
            <div className="history-marker-line">
              {markers.map((marker) => (
                <button
                  key={marker.id}
                  type="button"
                  className="history-marker-dot"
                  style={{ left: `${marker.offset}%` }}
                  onClick={() => onJumpToIndex?.(marker.index)}
                >
                  {marker.label}
                </button>
              ))}
            </div>
            <div className="history-marker-time end">11:59 PM</div>
          </div>

          <div className="history-table-wrap">
            <table className="history-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Time</th>
                  <th>Status</th>
                  <th>Location</th>
                  <th>Speed</th>
                  <th>Distance</th>
                  <th>Duration</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="history-table-empty">
                      No history rows available for this selection.
                    </td>
                  </tr>
                ) : (
                  visibleRows.map((row, index) => {
                    const active = Number(row.index) === Number(currentIndex);
                    return (
                      <tr key={row.id} className={active ? "active" : ""}>
                        <td>{row.sequence || pageStart + index}</td>
                        <td>{row.timeShort || row.time}</td>
                        <td>
                          <span className={`history-status-pill ${row.statusTone || "default"}`}>{row.status}</span>
                        </td>
                        <td className="history-location-cell" title={row.address}>
                          {row.address}
                        </td>
                        <td>{row.speed}</td>
                        <td>{row.distance}</td>
                        <td>{row.duration || "--"}</td>
                        <td>
                          <button type="button" className="history-table-play" onClick={() => onJumpToIndex?.(row.index)}>
                            <Play size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="history-panel-footer">
            <div className="history-panel-count">
              Showing {pageStart}-{pageEnd} of {filteredRows.length} records
            </div>
            <div className="history-pagination">
              <button
                type="button"
                className="history-pagination-nav"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={14} />
                Prev
              </button>

              <div className="history-pagination-pages">
                {pageNumbers.map((page, index) => {
                  const previous = pageNumbers[index - 1];
                  const showGap = previous && page - previous > 1;
                  return (
                    <React.Fragment key={page}>
                      {showGap ? <span className="history-pagination-gap">...</span> : null}
                      <button
                        type="button"
                        className={`history-pagination-page ${page === currentPage ? "active" : ""}`}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  );
                })}
              </div>

              <button
                type="button"
                className="history-pagination-nav"
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                Next
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </>
      ) : null}
    </section>
  );
}
